import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FRAME_OPTIONS, effectivePrintDpi, formatUsd } from '@lumina/shared';
import { api } from '../lib/api';
import { formatDate, formatDay } from '../lib/format';
import {
  AssignmentCell,
  hasInvalidOverride,
  pairStatus,
  toAssignmentList,
  type Assignments,
  type CatalogProduct,
  type CatalogSize,
} from '../components/PrintSizeAssignment';

interface MatrixPhoto {
  uuid: string;
  title: string;
  slug: string;
  thumb_url: string;
  width: number;
  height: number;
  is_active: number;
  price_base_cents: number;
  assignments: Assignments;
}

export function PrintSizes() {
  const [sizes, setSizes] = useState<CatalogSize[]>([]);
  const [photos, setPhotos] = useState<MatrixPhoto[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [showInactivePhotos, setShowInactivePhotos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api
      .get<{ sizes: CatalogSize[]; photos: MatrixPhoto[] }>('/api/admin/print-sizes/matrix')
      .then((d) => {
        setSizes(d.sizes);
        setPhotos(d.photos);
        setDirty(new Set());
      })
      .catch((err) => setError(err.message));

  useEffect(() => {
    load();
  }, []);

  const visiblePhotos = useMemo(
    () => photos.filter((p) => showInactivePhotos || p.is_active),
    [photos, showInactivePhotos]
  );

  const setAssignments = (uuid: string, next: Assignments) => {
    setPhotos((ps) => ps.map((p) => (p.uuid === uuid ? { ...p, assignments: next } : p)));
    setDirty((d) => new Set(d).add(uuid));
  };

  // Assigns every active size the photo meets at the size default; keeps
  // existing assignments and overrides (same as the API's auto-assign).
  const autoAssign = (photo: MatrixPhoto) => {
    const next = { ...photo.assignments };
    for (const s of sizes) {
      const dpi = effectivePrintDpi(photo, { widthIn: s.width_in, heightIn: s.height_in });
      if (s.is_active && dpi >= s.min_dpi && !next[s.id]) next[s.id] = { min_dpi_override: null };
    }
    setAssignments(photo.uuid, next);
  };

  const saveAll = async () => {
    const changed = photos.filter((p) => dirty.has(p.uuid));
    if (changed.some((p) => hasInvalidOverride(p.assignments))) {
      setError('DPI overrides must be between 72 and 600.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      for (const p of changed) {
        await api.put(`/api/admin/photos/${p.uuid}/print-sizes`, {
          assignments: toAssignmentList(p.assignments),
        });
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>Print sizes</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginTop: -8 }}>
        A photo is sold at a size when it's assigned, the size is active, the photo meets the
        minimum DPI (the size default, or a per-photo override), and Gelato products are set.
      </p>
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      <SizeCatalog sizes={sizes} onChanged={load} />
      <GelatoTemplateImport sizes={sizes} onApplied={load} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '32px 0 12px' }}>
        <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 400, margin: 0 }}>
          Photo assignments
        </h2>
        <label style={{ fontSize: 13, color: '#6b7280' }}>
          <input
            type="checkbox"
            checked={showInactivePhotos}
            onChange={(e) => setShowInactivePhotos(e.target.checked)}
          />{' '}
          Show inactive photos
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {dirty.size > 0 && (
            <span style={{ fontSize: 13, color: '#b45309' }}>
              {dirty.size} photo{dirty.size === 1 ? '' : 's'} changed
            </span>
          )}
          <button
            type="button"
            onClick={saveAll}
            disabled={saving || dirty.size === 0}
            style={{ ...btnPrimary, opacity: dirty.size === 0 ? 0.5 : 1 }}
          >
            {saving ? 'Saving…' : 'Save assignments'}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
        Tick to assign. Each cell shows the print-only price (base × the size's multiplier; frames
        cost extra). The number field lowers (or raises) the minimum DPI for that one pairing —
        leave it blank to use the size's default. Open a photo for per-frame costs and margins.
      </p>

      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Photo</th>
              {sizes.map((s) => (
                <th key={s.id} style={{ ...th, opacity: s.is_active ? 1 : 0.5 }}>
                  {s.label}
                  <div style={{ fontWeight: 400, fontSize: 11, color: '#6b7280' }}>
                    {s.is_active ? `min ${s.min_dpi} DPI` : 'inactive'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiblePhotos.map((p) => {
              const offered = sizes.filter((s) => pairStatus(p, s, p.assignments[s.id]).offered).length;
              return (
                <tr key={p.uuid} style={{ borderTop: '1px solid #f0f0f0', opacity: p.is_active ? 1 : 0.6 }}>
                  <td style={{ ...td, minWidth: 220 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <img
                        src={p.thumb_url}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <div>
                        <Link to={`/photos/${p.uuid}`} style={{ color: '#1a1a1a', fontWeight: 500 }}>
                          {p.title}
                        </Link>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {p.width}×{p.height}px · {formatUsd(p.price_base_cents)} base · {offered} offered
                          {dirty.has(p.uuid) && <span style={{ color: '#b45309' }}> · unsaved</span>}
                        </div>
                        <button type="button" onClick={() => autoAssign(p)} style={linkBtn}>
                          Assign all printable
                        </button>
                      </div>
                    </div>
                  </td>
                  {sizes.map((s) => (
                    <td key={s.id} style={td}>
                      <AssignmentCell
                        compact
                        photo={p}
                        size={s}
                        basePriceCents={p.price_base_cents}
                        assignment={p.assignments[s.id]}
                        onChange={(next) => {
                          const { [s.id]: _removed, ...rest } = p.assignments;
                          setAssignments(p.uuid, next ? { ...rest, [s.id]: next } : rest);
                        }}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Size catalog editor
// ──────────────────────────────────────────────────────────────────────────────

type SizeDraft = Pick<
  CatalogSize,
  'id' | 'label' | 'width_in' | 'height_in' | 'multiplier' | 'min_dpi' | 'sort_order' | 'is_active'
>;

/** Editable product fields per frame; cost is dollars as typed. */
type ProductDraft = Record<string, { gelato_product_uid: string; paper: string; cost: string }>;

const EMPTY_SIZE: CatalogSize = {
  id: '',
  label: '',
  width_in: 0,
  height_in: 0,
  multiplier: 1,
  min_dpi: 150,
  sort_order: 100,
  is_active: 0,
  products: {},
};

function sizeDraftOf(s: CatalogSize): SizeDraft {
  const { id, label, width_in, height_in, multiplier, min_dpi, sort_order, is_active } = s;
  return { id, label, width_in, height_in, multiplier, min_dpi, sort_order, is_active };
}

function productDraftOf(s: CatalogSize): ProductDraft {
  return Object.fromEntries(
    FRAME_OPTIONS.map((f) => {
      const p = s.products[f.id];
      return [
        f.id,
        {
          gelato_product_uid: p?.gelato_product_uid ?? '',
          paper: p?.paper ?? '',
          cost: p?.cost_cents != null ? (p.cost_cents / 100).toFixed(2) : '',
        },
      ];
    })
  );
}

function toCents(dollars: string): number | null | 'invalid' {
  if (!dollars.trim()) return null;
  const n = Number(dollars);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 'invalid';
}

function costRange(products: Record<string, CatalogProduct>) {
  const costs = Object.values(products)
    .map((p) => p.cost_cents)
    .filter((c): c is number => c != null);
  if (costs.length === 0) return null;
  const lo = Math.min(...costs);
  const hi = Math.max(...costs);
  return lo === hi ? formatUsd(lo) : `${formatUsd(lo)}–${formatUsd(hi)}`;
}

function SizeCatalog({ sizes, onChanged }: { sizes: CatalogSize[]; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, marginTop: 16 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>Id</th>
            <th style={th}>Label</th>
            <th style={th}>Width (in)</th>
            <th style={th}>Height (in)</th>
            <th style={th} title="Photo base price × this = print-only price for this size">Price × base</th>
            <th style={th}>Min DPI</th>
            <th style={th}>Sort</th>
            <th style={th}>Active</th>
            <th style={th}>Products</th>
            <th style={th}>Our cost</th>
            <th style={th}>Photos</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {sizes.map((s) => (
            <SizeRow key={s.id} size={s} isNew={false} onSaved={onChanged} />
          ))}
          {adding && (
            <SizeRow
              size={EMPTY_SIZE}
              isNew
              onSaved={() => {
                setAdding(false);
                onChanged();
              }}
              onCancel={() => setAdding(false)}
            />
          )}
        </tbody>
      </table>
      {!adding && (
        <div style={{ padding: 12, borderTop: '1px solid #f0f0f0' }}>
          <button type="button" onClick={() => setAdding(true)} style={btnSecondary}>
            Add size
          </button>
        </div>
      )}
    </div>
  );
}

function SizeRow({
  size,
  isNew,
  onSaved,
  onCancel,
}: {
  size: CatalogSize;
  isNew: boolean;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<SizeDraft>(() => sizeDraftOf(size));
  const [products, setProducts] = useState<ProductDraft>(() => productDraftOf(size));
  const [showProducts, setShowProducts] = useState(isNew);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(sizeDraftOf(size));
    setProducts(productDraftOf(size));
  }, [size]);

  const changed =
    isNew ||
    JSON.stringify(draft) !== JSON.stringify(sizeDraftOf(size)) ||
    JSON.stringify(products) !== JSON.stringify(productDraftOf(size));
  const set = <K extends keyof SizeDraft>(k: K, v: SizeDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setProduct = (frameId: string, patch: Partial<ProductDraft[string]>) =>
    setProducts((ps) => {
      const next = { ...ps[frameId], ...patch };
      // A different product has a different paper; it's re-read from the new uid on save.
      if (patch.gelato_product_uid !== undefined && patch.gelato_product_uid !== ps[frameId].gelato_product_uid) {
        next.paper = '';
      }
      return { ...ps, [frameId]: next };
    });

  const saved = Object.values(size.products);
  const productCount = saved.length;
  const checked = saved.filter((p) => p.gelato_ok !== null);
  const failed = checked.some((p) => p.gelato_ok === 0);
  const range = costRange(size.products);
  const missingCosts = saved.filter((p) => p.cost_cents == null).length;

  const verify = async () => {
    setVerifying(true);
    setError(null);
    try {
      await api.post(`/api/admin/print-sizes/${size.id}/gelato/verify`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const save = async () => {
    const productBody: Record<string, { gelato_product_uid: string; paper: string | null; cost_cents: number | null }> = {};
    for (const [frameId, p] of Object.entries(products)) {
      if (!p.gelato_product_uid.trim()) continue;
      const cents = toCents(p.cost);
      if (cents === 'invalid') {
        setError(`Cost for ${frameId} must be a dollar amount`);
        setShowProducts(true);
        return;
      }
      productBody[frameId] = { gelato_product_uid: p.gelato_product_uid, paper: p.paper || null, cost_cents: cents };
    }
    setBusy(true);
    setError(null);
    try {
      const { id, ...fields } = draft;
      if (isNew) await api.post('/api/admin/print-sizes', { ...draft, products: productBody });
      else await api.put(`/api/admin/print-sizes/${id}`, { ...fields, products: productBody });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr style={{ borderTop: '1px solid #f0f0f0', opacity: draft.is_active ? 1 : 0.65 }}>
        <td style={td}>
          {isNew ? (
            <input
              value={draft.id}
              placeholder="e.g. large_16x24"
              onChange={(e) => set('id', e.target.value.toLowerCase())}
              style={{ ...input, width: 120 }}
            />
          ) : (
            <code style={{ fontSize: 12 }}>{draft.id}</code>
          )}
        </td>
        <td style={td}>
          <input value={draft.label} onChange={(e) => set('label', e.target.value)} style={{ ...input, width: 90 }} />
        </td>
        <td style={td}>
          <NumInput value={draft.width_in} step={0.5} onChange={(v) => set('width_in', v)} />
        </td>
        <td style={td}>
          <NumInput value={draft.height_in} step={0.5} onChange={(v) => set('height_in', v)} />
        </td>
        <td style={td}>
          <NumInput value={draft.multiplier} step={0.1} onChange={(v) => set('multiplier', v)} />
        </td>
        <td style={td}>
          <NumInput value={draft.min_dpi} step={10} onChange={(v) => set('min_dpi', Math.round(v))} />
        </td>
        <td style={td}>
          <NumInput value={draft.sort_order} step={5} onChange={(v) => set('sort_order', Math.round(v))} />
        </td>
        <td style={td}>
          <input
            type="checkbox"
            checked={!!draft.is_active}
            onChange={(e) => set('is_active', e.target.checked ? 1 : 0)}
          />
        </td>
        <td style={td}>
          <button type="button" onClick={() => setShowProducts((v) => !v)} style={linkBtn}>
            <span
              style={{
                color: productCount === 0 ? '#b91c1c' : productCount < FRAME_OPTIONS.length ? '#b45309' : '#15803d',
              }}
            >
              {productCount}/{FRAME_OPTIONS.length} frames
            </span>
            <span
              style={{
                display: 'block',
                fontSize: 10,
                color: checked.length === 0 ? '#9ca3af' : failed ? '#b91c1c' : '#15803d',
              }}
            >
              {checked.length === 0 ? 'not verified' : failed ? 'verify failed' : 'verified'}
            </span>
          </button>
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {range ?? <span style={{ color: '#9ca3af' }}>unknown</span>}
          {range && missingCosts > 0 && (
            <span style={{ display: 'block', fontSize: 10, color: '#b45309' }}>{missingCosts} missing</span>
          )}
        </td>
        <td style={td}>{size.photo_count ?? '—'}</td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          <button type="button" onClick={save} disabled={busy || !changed} style={{ ...btnSmall, opacity: changed ? 1 : 0.4 }}>
            {busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
          {isNew && (
            <button type="button" onClick={onCancel} style={{ ...linkBtn, marginLeft: 8 }}>
              Cancel
            </button>
          )}
        </td>
      </tr>
      {(showProducts || error) && (
        <tr>
          <td colSpan={12} style={{ ...td, background: '#fafafa' }}>
            {error && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{error}</div>}
            {showProducts && !isNew && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 12, color: '#6b7280' }}>
                <button type="button" onClick={verify} disabled={verifying || productCount === 0} style={btnSmall}>
                  {verifying ? 'Checking…' : 'Verify with Gelato & fetch costs'}
                </button>
                {size.gelato_template_id && (
                  <span>
                    From template <code>{size.gelato_template_id}</code>
                  </span>
                )}
                {changed && <span style={{ color: '#b45309' }}>Save first — verify checks the saved products.</span>}
              </div>
            )}
            {showProducts && (
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={th}>Frame</th>
                    <th style={th}>Gelato productUid</th>
                    <th style={th}>Paper / texture</th>
                    <th style={th}>Our cost (USD, ex. shipping)</th>
                    <th style={th}>Shipping (US est.)</th>
                    <th style={th}>Customer pays for frame</th>
                    <th style={th}>Gelato check</th>
                  </tr>
                </thead>
                <tbody>
                  {FRAME_OPTIONS.map((f) => {
                    const d = products[f.id];
                    const savedProduct = size.products[f.id];
                    return (
                      <tr key={f.id} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{f.label}</td>
                        <td style={td}>
                          <input
                            value={d.gelato_product_uid}
                            placeholder="Not sold in this frame"
                            onChange={(e) => setProduct(f.id, { gelato_product_uid: e.target.value })}
                            style={{ ...input, width: '100%', minWidth: 260, fontFamily: 'monospace', fontSize: 11 }}
                          />
                        </td>
                        <td style={td}>
                          <input
                            value={d.paper}
                            placeholder={d.gelato_product_uid ? 'Read from uid on save' : ''}
                            disabled={!d.gelato_product_uid.trim()}
                            onChange={(e) => setProduct(f.id, { paper: e.target.value })}
                            style={{ ...input, width: 170 }}
                          />
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <span style={{ color: '#6b7280' }}>$ </span>
                          <input
                            value={d.cost}
                            inputMode="decimal"
                            placeholder="unknown"
                            disabled={!d.gelato_product_uid.trim()}
                            onChange={(e) => setProduct(f.id, { cost: e.target.value })}
                            style={{ ...input, width: 80 }}
                          />
                          {savedProduct?.cost_source && (
                            <span style={{ display: 'block', fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                              {savedProduct.cost_source === 'gelato' ? 'from Gelato' : 'entered manually'}
                              {savedProduct.cost_updated_at && ` · ${formatDay(savedProduct.cost_updated_at)}`}
                            </span>
                          )}
                        </td>
                        <td style={td}>
                          {savedProduct?.ship_estimate_cents != null ? (
                            <>
                              {formatUsd(savedProduct.ship_estimate_cents)}
                              <span style={{ display: 'block', fontSize: 10, color: '#6b7280' }}>
                                {savedProduct.ship_estimate_method}
                              </span>
                            </>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                        <td style={{ ...td, color: '#6b7280' }}>
                          {f.priceCents === 0 ? 'included' : `+${formatUsd(f.priceCents)}`}
                        </td>
                        <td style={td}>
                          {!savedProduct ? (
                            '—'
                          ) : savedProduct.gelato_ok === null ? (
                            <span style={{ color: '#9ca3af' }}>not checked</span>
                          ) : savedProduct.gelato_ok ? (
                            <span style={{ color: '#15803d' }}>✓ found</span>
                          ) : (
                            <span style={{ color: '#b91c1c' }}>✗ {savedProduct.gelato_error}</span>
                          )}
                          {savedProduct?.gelato_checked_at && (
                            <span style={{ display: 'block', fontSize: 10, color: '#9ca3af' }}>
                              {formatDate(savedProduct.gelato_checked_at)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Import productUids from a Gelato template
// ──────────────────────────────────────────────────────────────────────────────

interface TemplateVariant {
  id: string | null;
  title: string;
  productUid: string;
  options: { name: string; value: string }[];
  suggested: { size_id: string | null; frame_id: string | null };
}

interface VariantChoice {
  include: boolean;
  size_id: string;
  frame_id: string;
}

function GelatoTemplateImport({ sizes, onApplied }: { sizes: CatalogSize[]; onApplied: () => void }) {
  const [templateId, setTemplateId] = useState('');
  const [template, setTemplate] = useState<{ id: string; name: string; variants: TemplateVariant[] } | null>(null);
  const [choices, setChoices] = useState<VariantChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const loadTemplate = async () => {
    setBusy(true);
    setMessage(null);
    setTemplate(null);
    try {
      const d = await api.get<{ template: { id: string; name: string; variants: TemplateVariant[] } }>(
        `/api/admin/print-sizes/gelato/templates/${encodeURIComponent(templateId.trim())}`
      );
      setTemplate(d.template);
      setChoices(
        d.template.variants.map((v) => ({
          include: !!(v.suggested.size_id && v.suggested.frame_id),
          size_id: v.suggested.size_id ?? '',
          frame_id: v.suggested.frame_id ?? '',
        }))
      );
      if (d.template.variants.length === 0) {
        setMessage({ text: 'The template has no variants with a productUid.', error: true });
      }
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Could not load template', error: true });
    } finally {
      setBusy(false);
    }
  };

  const included = choices.filter((c) => c.include);
  const duplicate = (() => {
    const seen = new Set<string>();
    for (const c of included) {
      const key = `${c.size_id}/${c.frame_id}`;
      if (seen.has(key)) return key;
      seen.add(key);
    }
    return null;
  })();
  const incomplete = included.some((c) => !c.size_id || !c.frame_id);

  const apply = async () => {
    if (!template) return;
    setBusy(true);
    setMessage(null);
    try {
      const mappings = template.variants
        .map((v, i) => ({ v, c: choices[i] }))
        .filter(({ c }) => c.include)
        .map(({ v, c }) => ({ productUid: v.productUid, size_id: c.size_id, frame_id: c.frame_id }));
      const r = await api.post<{ mappings: number; sizes: string[] }>(
        `/api/admin/print-sizes/gelato/templates/${encodeURIComponent(template.id)}/apply`,
        { mappings }
      );
      setMessage({
        text: `Applied ${r.mappings} productUid(s) to ${r.sizes.join(', ')}. Verify each size to confirm with Gelato.`,
        error: false,
      });
      setTemplate(null);
      onApplied();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Apply failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const setChoice = (i: number, patch: Partial<VariantChoice>) =>
    setChoices((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, marginTop: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>Import from Gelato template</strong>
        <input
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          placeholder="Template id (Gelato dashboard → Templates)"
          style={{ ...input, width: 340 }}
        />
        <button type="button" onClick={loadTemplate} disabled={busy || !templateId.trim()} style={btnSmall}>
          {busy && !template ? 'Loading…' : 'Load variants'}
        </button>
        <a
          href="https://dashboard.gelato.com/templates"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 12, color: '#0f766e' }}
        >
          Open Gelato templates ↗
        </a>
      </div>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0' }}>
        Pulls each variant's productUid and suggests the matching size and frame from its dimensions and
        finish. Review, then apply — only the mapped frames of each size are changed.
      </p>
      {message && (
        <p style={{ fontSize: 13, color: message.error ? '#b91c1c' : '#15803d', margin: '8px 0 0' }}>{message.text}</p>
      )}
      {template && template.variants.length > 0 && (
        <>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, marginTop: 12 }}>
            <thead>
              <tr>
                <th style={th} />
                <th style={th}>Variant ({template.name})</th>
                <th style={th}>productUid</th>
                <th style={th}>Size</th>
                <th style={th}>Frame</th>
              </tr>
            </thead>
            <tbody>
              {template.variants.map((v, i) => (
                <tr key={v.productUid + i} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={choices[i].include}
                      onChange={(e) => setChoice(i, { include: e.target.checked })}
                    />
                  </td>
                  <td style={td}>
                    {v.title || '—'}
                    {v.options.length > 0 && (
                      <div style={{ color: '#6b7280', fontSize: 11 }}>
                        {v.options.map((o) => `${o.name}: ${o.value}`).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', maxWidth: 360 }}>
                    {v.productUid}
                  </td>
                  <td style={td}>
                    <select
                      value={choices[i].size_id}
                      onChange={(e) => setChoice(i, { size_id: e.target.value, include: true })}
                      style={input}
                    >
                      <option value="">— choose —</option>
                      {sizes.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label} ({s.id})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={td}>
                    <select
                      value={choices[i].frame_id}
                      onChange={(e) => setChoice(i, { frame_id: e.target.value, include: true })}
                      style={input}
                    >
                      <option value="">— choose —</option>
                      {FRAME_OPTIONS.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <button
              type="button"
              onClick={apply}
              disabled={busy || included.length === 0 || incomplete || !!duplicate}
              style={btnPrimary}
            >
              {busy ? 'Applying…' : `Apply ${included.length} mapping${included.length === 1 ? '' : 's'}`}
            </button>
            {incomplete && <span style={{ fontSize: 12, color: '#b45309' }}>Choose a size and frame for every ticked variant.</span>}
            {duplicate && <span style={{ fontSize: 12, color: '#b91c1c' }}>Two variants map to {duplicate}.</span>}
          </div>
        </>
      )}
    </div>
  );
}

function NumInput({ value, step, onChange }: { value: number; step: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={{ ...input, width: 70 }}
    />
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 8px',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  borderBottom: '1px solid #e5e5e5',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = { padding: '8px', verticalAlign: 'top' };

const input: React.CSSProperties = {
  padding: '4px 6px',
  border: '1px solid #e5e5e5',
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #d4d4d4',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSmall: React.CSSProperties = { ...btnSecondary, padding: '4px 10px', fontSize: 12 };

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 11,
  color: '#2563eb',
  cursor: 'pointer',
};
